import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";

/* Static, plain-English terms of service. */
export default function Terms() {
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" data-testid="terms-page">
      <SEO
        title="Terms of Service — Gr8Boost ERP"
        description="Plain-English terms of service for Gr8Boost ERP. 7-day free trial, annual subscription, data ownership, fair-use and termination policy."
        canonical="https://gr8booster.com/terms"
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF4F00] font-mono">Legal · v1.0</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mt-2">Terms of service</h1>
        <p className="text-[#495057] mt-3 text-sm">Effective February 2026 · Gr8booster</p>

        <Section title="1. The deal">
          <p>You give us your email and team data. We give you a cloud ERP that runs 24/7. You can cancel any time.</p>
        </Section>
        <Section title="2. Your free trial">
          <p>Every new workspace gets <strong>7 days of full access</strong>, no credit card required. After day 7, writes (creating invoices, posting journals etc.) are blocked until you upgrade. You can still read your data.</p>
        </Section>
        <Section title="3. Paid subscriptions">
          <ul className="list-disc ml-5 space-y-1.5">
            <li>Annual billing only. Pricing is quote-based — request a quote from Billing → Upgrade.</li>
            <li>Payment processed by PayPal, Stripe or MTN MoMo. Receipts are emailed.</li>
            <li>If you don&apos;t renew on time, your workspace goes back to read-only — your data is never deleted automatically.</li>
          </ul>
        </Section>
        <Section title="4. Your data is yours">
          <p>You own everything you put into your workspace. We act as a custodian. You can export at any time via CSV/JSON. We will delete your data within 30 days of a written deletion request.</p>
        </Section>
        <Section title="5. Acceptable use">
          <p>Don&apos;t use Gr8Boost ERP for anything illegal, don&apos;t try to break into other workspaces, don&apos;t spam. We reserve the right to suspend workspaces that abuse the platform.</p>
        </Section>
        <Section title="6. Uptime & support">
          <p>We aim for 99.9% uptime. If we miss it badly in any month, contact us and we&apos;ll credit pro-rata. Support is via email — replies within 1 business day.</p>
        </Section>
        <Section title="7. Liability">
          <p>Gr8Boost ERP is provided &ldquo;as-is&rdquo;. Our maximum liability in any 12-month period is capped at the fees you paid us in the prior 12 months. We exclude indirect or consequential damages, to the extent allowed by law.</p>
        </Section>
        <Section title="8. Changes to these terms">
          <p>If we make a material change, we&apos;ll email all workspace admins at least 30 days before it takes effect.</p>
        </Section>
        <Section title="9. Contact">
          <p>Questions? Email <a className="underline" href="mailto:legal@gr8booster.com">legal@gr8booster.com</a>.</p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-heading text-xl font-bold tracking-tight">{title}</h2>
      <div className="text-[#495057] text-[15px] leading-relaxed mt-3">{children}</div>
    </section>
  );
}

function Header() {
  return (
    <header className="border-b border-[#DEE2E6]">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><Logo size={28} /></Link>
        <Link to="/" className="text-sm text-[#495057] hover:text-[#FF4F00]">← Back to gr8booster.com</Link>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#DEE2E6] py-8">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-[10px] uppercase tracking-[0.2em] text-[#495057] flex flex-wrap gap-x-6 gap-y-2 justify-between">
        <div>© {new Date().getFullYear()} Gr8booster · Terms of service</div>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-[#0A0A0A]">Privacy</Link>
          <Link to="/" className="hover:text-[#0A0A0A]">Home</Link>
        </div>
      </div>
    </footer>
  );
}
